/* ---------------------------------------------------------------------------
 * Product Selector — app wrapper (Shopify port)
 *
 * Behaviour is a straight port of the original selector. What changed for
 * Shopify, and only this:
 *
 *   1. jQuery is loaded from the theme's assets and captured privately with
 *      noConflict(true), so nothing is added to window and a theme that already
 *      ships jQuery is untouched.
 *   2. Image paths resolve against window.DBG_ASSET_BASE (the theme's asset
 *      CDN folder) instead of ./dbg-images/.
 *   3. PriceSpider "Shop Now" -> Shopify. Depending on the section setting it
 *      either adds the product's variant to the cart via /cart/add.js or links
 *      to the product page.
 *   4. Bazaarvoice inline ratings -> stars rendered from the "rating" value in
 *      dbg-data.js (or from a Shopify metafield you map onto it).
 *   5. Products with a "handle" are hydrated from /products/<handle>.js, so
 *      title, image, price and availability stay in sync with the store. Cards
 *      render immediately from the static data and are patched in place when
 *      the fetch resolves, so nothing blocks the result transition.
 * ------------------------------------------------------------------------- */

(function () {
    'use strict';

    var jQuery = window.jQuery;
    if (window.dbgJQuery) { jQuery = window.dbgJQuery; }
    if (!jQuery) {
        if (window.console) { console.warn('[dbg] jQuery not found — product selector disabled.'); }
        return;
    }
    var $ = jQuery;

    /* ----------------------------------------------------------------------
     * jquery.actual — used for measuring slides that are not yet visible.
     * Copyright 2012, Ben Lin (http://dreamerslab.com/), MIT licensed.
     * Version 1.0.19. AMD wrapper removed; bound to our private jQuery.
     * -------------------------------------------------------------------- */
    (function ($) {
        $.fn.addBack = $.fn.addBack || $.fn.andSelf;

        $.fn.extend({
            actual: function (method, options) {
                if (!this[method]) {
                    throw '$.actual => The jQuery method "' + method + '" you called does not exist';
                }

                var defaults = {
                    absolute: false,
                    clone: false,
                    includeMargin: false,
                    display: 'block'
                };

                var configs = $.extend(defaults, options);

                var $target = this.eq(0);
                var fix, restore;

                if (configs.clone === true) {
                    fix = function () {
                        var style = 'position: absolute !important; top: -1000 !important; ';
                        $target = $target.clone().attr('style', style).appendTo('body');
                    };

                    restore = function () {
                        $target.remove();
                    };
                } else {
                    var tmp = [];
                    var style = '';
                    var $hidden;

                    fix = function () {
                        $hidden = $target.parents().addBack().filter(':hidden');
                        style += 'visibility: hidden !important; display: ' + configs.display + ' !important; ';

                        if (configs.absolute === true) style += 'position: absolute !important; ';

                        $hidden.each(function () {
                            var $this = $(this);
                            var thisStyle = $this.attr('style');

                            tmp.push(thisStyle);
                            $this.attr('style', thisStyle ? thisStyle + ';' + style : style);
                        });
                    };

                    restore = function () {
                        $hidden.each(function (i) {
                            var $this = $(this);
                            var _tmp = tmp[i];

                            if (_tmp === undefined) {
                                $this.removeAttr('style');
                            } else {
                                $this.attr('style', _tmp);
                            }
                        });
                    };
                }

                fix();
                var actual = /(outer)/.test(method) ?
                    $target[method](configs.includeMargin) :
                    $target[method]();

                restore();
                return actual;
            }
        });
    }($));

    /* ----------------------------------------------------------------------
     * Settings + helpers
     * -------------------------------------------------------------------- */
    var settings = $.extend({
        assetBase: '',
        rootUrl: '/',
        moneyFormat: '${{amount}}',
        shopNowBehavior: 'product',   // 'product' | 'cart'
        afterAddToCart: 'cart',       // 'cart' | 'stay'
        linkTarget: '_self',
        showPrice: true,
        showRating: false,
        useShopifyTitle: false,   // false keeps the curated name from dbg-data.js (with its <sup>TM</sup> markup)
        useShopifyImage: false,
        loadingDelay: 500,
        shopNowLabel: 'Shop Now',
        addToCartLabel: 'Add to Cart',
        learnMoreLabel: 'Learn More',
        soldOutLabel: 'Sold Out'
    }, window.DBG_SETTINGS || {});

    var $url = window.location.pathname;
    var dbgPref, dbgProduct, dbgTransition;

    function dbgAsset(file) {
        if (!file) { return ''; }
        if (/^(https?:)?\/\//.test(file) || file.charAt(0) === '/') { return file; }
        return settings.assetBase + file.replace(/^\.?\//, '').replace(/^dbg-images\/[^/]+\//, '');
    }

    function dbgTrack(action, label) {
        if (typeof window.ga !== 'undefined') {
            window.ga('send', 'event', $url, action, label);
        }
        if (window.dataLayer && typeof window.dataLayer.push === 'function') {
            window.dataLayer.push({ event: 'product_selector', action: action, label: label });
        }
        document.dispatchEvent(new CustomEvent('dbg:track', { detail: { action: action, label: label } }));
    }

    function dbgFormatMoney(cents) {
        if (cents === null || typeof cents === 'undefined') { return ''; }
        var format = settings.moneyFormat;

        function fmt(number, precision, thousands, decimal) {
            number = (number / 100).toFixed(precision);
            var parts = number.split('.');
            var head = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
            return precision && parts[1] ? head + decimal + parts[1] : head;
        }

        var value = '';
        var placeholder = /\{\{\s*(\w+)\s*\}\}/;
        var match = format.match(placeholder);
        switch (match && match[1]) {
            case 'amount_no_decimals':
                value = fmt(cents, 0, ',', '.');
                break;
            case 'amount_with_comma_separator':
                value = fmt(cents, 2, '.', ',');
                break;
            case 'amount_no_decimals_with_comma_separator':
                value = fmt(cents, 0, '.', ',');
                break;
            default:
                value = fmt(cents, 2, ',', '.');
        }
        return format.replace(placeholder, value);
    }

    function dbgFindProduct(id) {
        for (var i = 0; i < dbgProducts.length; i++) {
            if (dbgProducts[i].id === id) { return dbgProducts[i]; }
        }
        return null;
    }

    /* ----------------------------------------------------------------------
     * Shopify hydration — keeps cards in sync with the store
     * -------------------------------------------------------------------- */
    var dbgHydrated = {};

    function dbgHydrate(product) {
        if (!product || !product.handle) { return $.Deferred().reject().promise(); }
        if (dbgHydrated[product.handle]) { return dbgHydrated[product.handle]; }

        var url = settings.rootUrl.replace(/\/$/, '') + '/products/' + product.handle + '.js';
        dbgHydrated[product.handle] = $.ajax({ url: url, dataType: 'json' }).then(function (live) {
            var variant = null;
            if (product.variantId) {
                for (var i = 0; i < live.variants.length; i++) {
                    if (String(live.variants[i].id) === String(product.variantId)) { variant = live.variants[i]; break; }
                }
            }
            if (!variant) {
                for (var j = 0; j < live.variants.length; j++) {
                    if (live.variants[j].available) { variant = live.variants[j]; break; }
                }
            }
            if (!variant) { variant = live.variants[0]; }

            product.live = {
                title: live.title,
                url: settings.rootUrl.replace(/\/$/, '') + '/products/' + live.handle,
                image: live.featured_image || (live.images && live.images[0]) || '',
                price: variant ? variant.price : live.price,
                compareAt: variant ? variant.compare_at_price : null,
                variantId: variant ? variant.id : null,
                available: !!(variant && variant.available)
            };
            return product.live;
        }, function () {
            if (window.console) { console.warn('[dbg] could not hydrate product handle "' + product.handle + '" — using static data.'); }
        });

        return dbgHydrated[product.handle];
    }

    function dbgApplyLive(product) {
        var live = product.live;
        if (!live) { return; }
        var $card = $('#dbg').find('.dbg-product[data-product-id="' + product.id + '"]');
        if (!$card.length) { return; }

        if (live.image && settings.useShopifyImage) { $card.find('figure img').attr('src', live.image); }
        if (live.title && settings.useShopifyTitle) { $card.find('.product-title h3').html(live.title); }
        if (live.url) { $card.find('a[data-role="info"]').attr('href', live.url); }

        if (settings.showPrice && live.price !== null && typeof live.price !== 'undefined') {
            var priceHtml = dbgFormatMoney(live.price);
            if (live.compareAt && live.compareAt > live.price) {
                priceHtml += ' <s>' + dbgFormatMoney(live.compareAt) + '</s>';
            }
            $card.find('.dbg-price').html(priceHtml).show();
        }

        var $shop = $card.find('.dbg-pdb');
        if ($shop.length) {
            $shop.attr('data-variant-id', live.variantId || '');
            if (!live.available) {
                $shop.addClass('disabled').text(settings.soldOutLabel);
            }
        }

        dbgAlignBlocks();
    }

    function dbgStars(rating) {
        if (!settings.showRating || !rating) { return ''; }
        var value = parseFloat(rating) || 0;
        var pct = Math.max(0, Math.min(100, (value / 5) * 100));
        var star = function (file) {
            return '<span class="star"><img src="' + dbgAsset(file) + '" alt="" /></span>';
        };
        var row = function (file) { return star(file) + star(file) + star(file) + star(file) + star(file); };

        return '<div class="dbg-star-rating">' +
            '<div class="dbg-stars" role="img" aria-label="' + value + ' out of 5 stars">' +
            row('dbg-star.svg') +
            '<div class="dbg-stars-fill" style="width:' + pct + '%">' + row('dbg-star-fill.svg') + '</div>' +
            '</div>' +
            '<span class="dbg-rating-value">' + value + '</span>' +
            '</div>';
    }

    /* ----------------------------------------------------------------------
     * Result routing (unchanged from the original)
     * -------------------------------------------------------------------- */
    function dbgResolveResult(rawResult) {
        return rawResult;
    }

    $(document).ready(function () {
        if (parseInt(dbgGetUrlParameter('n'), 10) === 0) {
            $('#dbg').addClass('no-name');
        }

        // navigation triggers
        $('#dbg .next').click(function (e) {
            var $name = $('#dbg #dbg-name');
            if ($name.length && $name.val().length < 1) {
                $('#dbg h1 .dbg-no-name-option').addClass('show');
                $('#dbg .dbg-name-option').addClass('hide');
            } else {
                $('#dbg h1 .dbg-no-name-option').removeClass('show');
                $('#dbg .dbg-name-option').removeClass('hide');
            }
            if ($(this).attr('data-result')) {
                var resolvedResult = dbgResolveResult($(this).attr('data-result'));
                $('#dbg-review .dbg-pagination .results')
                    .attr('data-result', resolvedResult)
                    .removeClass('disabled');
            }

            var dbgDest = $(this).attr('data-next');
            var dbgCurrent = '#' + $('#dbg').find('.dbg-slide.current').attr('id');
            dbgNavigation(1000, dbgCurrent, dbgDest);

            // loop through review screen to be sure correct options appear there
            $('#dbg').find('.dbg-review-options' + dbgDest + '-review').addClass('active');
            var opt = $(this).attr('data-opt');
            if (opt) {
                var selected = $('#dbg').find('.dbg-review-options' + dbgCurrent + '-review ul button[data-opt=' + opt + ']').clone();
                $('#dbg').find('.dbg-review-options' + dbgCurrent + '-review .dbg-dropdown dt button').remove();
                $('#dbg').find('.dbg-review-options' + dbgCurrent + '-review .dbg-dropdown dt').append(selected);
                $('#dbg').find('.dbg-review-options' + dbgCurrent + '-review button[data-opt=' + opt + ']').addClass('selected');
                $('#dbg').find('.dbg-review-options' + dbgCurrent + '-review button[data-opt!=' + opt + ']').removeClass('selected');
            }

            dbgTrack('click', $(this).text());

            e.preventDefault();
            e.stopPropagation();
        });

        $('#dbg .back').click(function (e) {
            var dbgDest = $(this).attr('data-prev');
            var dbgCurrent = '#' + $('#dbg').find('.dbg-slide.current').attr('id');
            $('#dbg').find('.dbg-review-options' + dbgCurrent + '-review').removeClass('active');
            $('#dbg').find('.dbg-review-options' + dbgCurrent + '-review').find('dt button').remove();
            $('#dbg').find('.dbg-review-options' + dbgCurrent + '-review').find('ul button').removeClass('selected');
            $('#dbg').find('.dbg-review-options' + dbgCurrent + '-review').find('dt').append('<button class="null">Select answer</button>');

            dbgNavigation(1000, dbgCurrent, dbgDest);
            dbgTrack('click', 'Back');
            e.preventDefault();
            e.stopPropagation();
        });

        $('#dbg .result').click(function (e) {
            var dbgCurrent = '#' + $('#dbg').find('.dbg-slide.current').attr('id');
            dbgNavigation(1000, dbgCurrent, '#dbg-review');
            dbgTrack('click', 'Show Results: ' + $(this).text());

            // reset the results template
            $('#dbg-review .dbg-pagination .results').removeClass('disabled');
            var $dbgPrev = $(this).parents('.dbg-slide').attr('id');
            $('#dbg').find('#dbg-result .dbg-pagination .back').attr('data-prev', '#' + $dbgPrev);

            e.preventDefault();
            e.stopPropagation();
        });

        $('#dbg-review .results').click(function (e) {
            if ($(this).hasClass('disabled')) { e.preventDefault(); return; }

            // show the loading slide
            $('#dbg').find('.dbg-slide.current').removeClass('current');
            $('#dbg').find('#dbg-loading').addClass('current');
            dbgTrack('click', 'Show Results: ' + $(this).text());

            // reset the results template
            var $result = $(this).attr('data-result');
            $('#dbg').find('#dbg-result #dbg-result-name').html('');
            $('#dbg').find('#dbg-result .dbg-grid').html('');

            var matched = false;
            for (var i = 0; i < dbgResults.length; i++) {   // loop through results array
                if (dbgResults[i].name === $result) {       // find result that matches
                    matched = true;
                    var suggestions = dbgResults[i].suggestions || [];
                    var regex = new RegExp('^[aeiou].*', 'i');
                    var article = regex.test($result) ? 'an' : 'a';
                    $('#dbg').find('#dbg-result #dbg-result-name').html(article + ' ' + dbgResults[i].name);

                    for (var n = 0; n < dbgResults[i].products.length; n++) {
                        dbgProduct = dbgResults[i].products[n].id;
                        dbgAddProduct(dbgProduct, false);
                    }
                    if (suggestions.length >= 1) {
                        $('#dbg').find('#dbg-result .suggestion').removeClass('hide');
                        for (var s = 0; s < suggestions.length; s++) {
                            dbgProduct = suggestions[s].id;
                            dbgAddProduct(dbgProduct, true);
                        }
                    } else {
                        $('#dbg').find('#dbg-result .suggestion').addClass('hide');
                    }
                    break;
                }
            }

            if (!matched && window.console) {
                console.warn('[dbg] no entry in dbgResults named "' + $result + '" — result slide will be empty.');
            }

            dbgTrack('view_result', $result);

            // fix heights of product blocks
            dbgAlignBlocks();
            // set the back button destination
            var $dbgPrev = $(this).parents('.dbg-slide').attr('id');
            $('#dbg').find('#dbg-result .dbg-pagination .back').attr('data-prev', '#' + $dbgPrev);
            // setup transition to results page
            dbgNavigation(settings.loadingDelay, '#dbg-loading', '#dbg-result');
            e.preventDefault();
            e.stopPropagation();
        });

        // reset the app
        $('#dbg .dbg-restart').click(function (e) {
            $('html, body').animate({ scrollTop: '0px' }, 400, function () {
                $('#dbg').find('.dbg-slide.current').removeClass('current');
                $('#dbg').height($('#dbg').find('#dbg-step-2').find('.dbg-container').actual('outerHeight'));
                $('#dbg').find('#dbg-step-2').addClass('current');
                $('#dbg').find('.dbg-review-options').removeClass('active');
                $('#dbg').find('.dbg-review-options').find('dt button').remove();
                $('#dbg').find('.dbg-review-options').find('ul button').removeClass('selected');
                $('#dbg').find('.dbg-review-options').find('dt').append('<button class="null">Select answer</button>');
                $('#dbg').find('#dbg-step-2-review').addClass('active');
                $('#dbg-review .dbg-pagination .results').addClass('disabled').removeAttr('data-result');

                dbgPref = '';
            });
            dbgTrack('click', 'Restart');

            e.preventDefault();
            e.stopPropagation();
        });

        // form functions
        $('#dbg #dbg-name').on('input', function () {
            if ($(this).val().length > 1) {
                $(this).parents('.dbg-options').find('button').removeClass('disabled');
                $('#dbg span.dbg-name-slot').addClass('show');
            } else {
                $(this).parents('.dbg-options').find('button').addClass('disabled');
                $('#dbg span.dbg-name-slot').removeClass('show');
            }
            $('#dbg span.dbg-name-slot span').html($(this).val());
        });

        /* ------------------------------------------------------------------
         * "Shop Now" — was a PriceSpider retailer lightbox, now Shopify.
         * ---------------------------------------------------------------- */
        $(document).on('click', '.dbg-pdb', function (e) {
            e.preventDefault();
            e.stopPropagation();

            var $btn = $(this);
            if ($btn.hasClass('disabled') || $btn.hasClass('is-busy')) { return; }

            var productId = $btn.attr('data-product-id');
            var product = dbgFindProduct(productId);
            var eventLabel = 'shop now button:' + (productId ? ' ' + productId : '');

            if (settings.shopNowBehavior !== 'cart') {
                dbgTrack('click-shop-now', eventLabel);
                var href = (product && product.live && product.live.url) || $btn.attr('data-href') || (product && product.infoUrl) || '/';
                if (settings.linkTarget === '_blank') { window.open(href, '_blank'); } else { window.location.href = href; }
                return;
            }

            var variantId = $btn.attr('data-variant-id');
            if (!variantId) {
                if (window.console) { console.warn('[dbg] no variant for "' + productId + '" — falling back to the product page.'); }
                window.location.href = (product && product.infoUrl) || '/';
                return;
            }

            var original = $btn.text();
            $btn.addClass('is-busy').text('Adding…');

            $.ajax({
                url: settings.rootUrl.replace(/\/$/, '') + '/cart/add.js',
                type: 'POST',
                dataType: 'json',
                contentType: 'application/json',
                data: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
            }).done(function () {
                dbgTrack('add-to-cart', eventLabel);
                document.dispatchEvent(new CustomEvent('dbg:cart:added', { detail: { productId: productId, variantId: variantId } }));

                if (settings.afterAddToCart === 'cart') {
                    window.location.href = settings.rootUrl.replace(/\/$/, '') + '/cart';
                    return;
                }
                // let the theme's cart drawer / bubble refresh itself
                document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
                $btn.removeClass('is-busy').text('Added ✓');
                setTimeout(function () { $btn.text(original); }, 2500);
            }).fail(function (xhr) {
                var message = (xhr && xhr.responseJSON && xhr.responseJSON.description) || 'Could not add to cart.';
                if (window.console) { console.error('[dbg] add to cart failed:', message); }
                $btn.removeClass('is-busy').text(original);
                window.alert(message);
            });
        });

        $(document).on('click', '.dbg-btn.learn-more', function () {
            var eventLabel = 'learn more:';
            eventLabel += typeof $(this).data('productId') !== 'undefined' ? (' ' + $(this).data('productId')) : '';
            dbgTrack('click', eventLabel);
        });

        // animate corresponding svg's on option hover
        $('#dbg .dbg-options .dbg-btn').hover(function () {
            if (!$(this).hasClass('off')) {
                if ($(this).parents('.dbg-slide').find('.dbg-block-illo div').length > 1) {
                    var $index = parseInt($(this).parents('div').index(), 10) + 1;
                    $(this).parents('.dbg-slide').find('.dbg-block-illo div:nth-of-type(' + $index + ')').addClass('highlight');
                } else {
                    $(this).parents('.dbg-slide').find('.dbg-block-illo div').addClass('highlight');
                }
            }
        }, function () {
            if ($(this).parents('.dbg-slide').find('.dbg-block-illo div').length > 1) {
                var $index = parseInt($(this).parents('div').index(), 10) + 1;
                $(this).parents('.dbg-slide').find('.dbg-block-illo div:nth-of-type(' + $index + ')').removeClass('highlight');
            } else {
                $(this).parents('.dbg-slide').find('.dbg-block-illo div').removeClass('highlight');
            }
        });

        // warm the Shopify product cache so result cards are accurate on arrival
        for (var p = 0; p < dbgProducts.length; p++) {
            (function (product) {
                if (!product.handle) { return; }
                dbgHydrate(product).done(function () { dbgApplyLive(product); });
            }(dbgProducts[p]));
        }

        // review screen dropdowns
        $(document).on('click', '.dbg-dropdown, .dbg-dropdown ul button', function (e) {
            if ($(this).parents('.dbg-review-options').hasClass('dbg-dropdown-expand')) {
                $('#dbg-review').removeClass('dbg-editing');
                $(this).parents('.dbg-review-options').removeClass('dbg-dropdown-expand');
            } else {
                $('#dbg-review .dbg-review-options').removeClass('dbg-dropdown-expand');
                $('#dbg-review').addClass('dbg-editing');
                $(this).parents('.dbg-review-options').addClass('dbg-dropdown-expand');
            }
            e.preventDefault();
        });

        $(document).on('click', '.dbg-dropdown ul button', function (e) {
            $(this).parents('.dbg-dropdown').find('.selected').removeClass('selected');
            var selected = $(this).clone();

            $(this).addClass('selected');
            $(this).parents('.dbg-dropdown').find('dt button').remove();
            $(this).parents('.dbg-dropdown').find('dt').append(selected);
            if ($(this).parents('.dbg-review-options').hasClass('dbg-dropdown-expand')) {
                $('#dbg-review').removeClass('dbg-editing');
                $(this).parents('.dbg-review-options').removeClass('dbg-dropdown-expand');
            } else {
                $('#dbg-review .dbg-review-options').removeClass('dbg-dropdown-expand');
                $('#dbg-review').addClass('dbg-editing');
                $(this).parents('.dbg-review-options').addClass('dbg-dropdown-expand');
            }
            dbgTrack('click', $(this).text());

            var dbgDest = $(this).attr('data-next');

            // loop through review screen to be sure correct options appear there
            $(this).parents('.dbg-review-options').nextAll('.dbg-review-options').removeClass('active');
            $('#dbg').find('.dbg-review-options:not(.active)').find('dt button').remove();
            $('#dbg').find('.dbg-review-options:not(.active)').find('ul button').removeClass('selected');
            $('#dbg').find('.dbg-review-options:not(.active)').find('dt').append('<button class="null">Select answer</button>');
            if (dbgDest) {
                $('#dbg').find('.dbg-review-options' + dbgDest).addClass('active');
            }

            if ($(this).hasClass('review-result')) {
                var resolvedResult = dbgResolveResult($(this).attr('data-result'));
                $('#dbg-review .dbg-pagination .results').attr('data-result', resolvedResult);
                $('#dbg-review .dbg-pagination .results').removeClass('disabled');
            } else {
                $('#dbg-review .dbg-pagination .results').addClass('disabled');
            }
            $('#dbg').height($('#dbg').find('#dbg-review').find('.dbg-container').actual('outerHeight'));
            e.preventDefault();
        });

        $(document).bind('click', function (e) {
            var $clicked = $(e.target);
            if ($clicked.parents('.dbg-dropdown').length != 1 && !$clicked.hasClass('dbg-dropdown')) {
                $('#dbg-review .dbg-review-options').removeClass('dbg-dropdown-expand');
                $('#dbg-review').removeClass('dbg-editing');
            }
        });
    });

    $(window).on('load', function () {
        // reveal
        $('#dbg').addClass('ready');
        $('#dbg-step-2').addClass('current');
        $('#dbg').height($('#dbg #dbg-step-2 .dbg-container').outerHeight());
        // preload
        for (var i = 0; i < dbgProducts.length; i++) {
            $('<img/>')[0].src = dbgAsset(dbgProducts[i].imageSrc);
        }
    }).on('resize', function () {
        if ($('#dbg-result').hasClass('current')) {
            dbgAlignBlocks();
        }
        $('#dbg').height($('#dbg').find('.dbg-slide.current .dbg-container').actual('outerHeight'));
    });

    function dbgNavigation(delay, current, destination) {
        $('html,body').animate({ scrollTop: '0px' }, 400, function () {
            clearTimeout(dbgTransition);
            if (!$('#dbg').find('#dbg-loading').hasClass('current')) {
                $('#dbg').find(current).addClass('anim-out');
                $('#dbg').find(current).removeClass('current');
            }
            dbgTransition = setTimeout(function () {
                $('#dbg').height($('#dbg').find(destination).find('.dbg-container').actual('outerHeight'));
                $('#dbg').find(current).removeClass('anim-out');
                $('#dbg').find(current).removeClass('current');
                $('#dbg').find(destination).addClass('current');
            }, delay);
        });
    }

    function dbgAlignBlocks() {
        $('#dbg-result .dbg-grid.primary .dbg-product .dbg-detail').attr('style', '');
        var $height = maxHeight($('#dbg-result .dbg-grid.primary .dbg-product .dbg-detail'));
        if ($height > 0 && isFinite($height)) {
            $('#dbg-result .dbg-grid.primary .dbg-product .dbg-detail').height($height);
        }
    }

    var maxHeight = function (elems) {
        if (!elems.length) { return 0; }
        return Math.max.apply(null, elems.map(function () {
            return $(this).height();
        }).get());
    };

    function dbgAddProduct(product, suggestion) {
        for (var i = 0; i < dbgProducts.length; i++) {
            if (dbgProducts[i].id === product) {
                var item = dbgProducts[i];
                var live = item.live || {};

                var productName = (settings.useShopifyTitle && live.title) ? live.title : item.name;
                var productDescription = item.description;
                var productImage = (settings.useShopifyImage && live.image) ? live.image : dbgAsset(item.imageSrc);
                var productImageAlt = item.imageAlt;
                var productColor = item.color;
                var productInfo = live.url || item.infoUrl;
                var target = settings.linkTarget;

                var ratingHtml = dbgStars(item.rating);
                var priceHtml = '';
                if (settings.showPrice && typeof live.price !== 'undefined' && live.price !== null) {
                    priceHtml = dbgFormatMoney(live.price);
                    if (live.compareAt && live.compareAt > live.price) {
                        priceHtml += ' <s>' + dbgFormatMoney(live.compareAt) + '</s>';
                    }
                }
                var priceBlock = '<p class="dbg-price"' + (priceHtml ? '' : ' style="display:none"') + '>' + priceHtml + '</p>';

                var shopLabel = settings.shopNowBehavior === 'cart' ? settings.addToCartLabel : settings.shopNowLabel;
                var soldOut = live.available === false;

                var shopBtn = '<a href="' + productInfo + '" data-product-id="' + item.id + '"' +
                    ' data-variant-id="' + (live.variantId || '') + '"' +
                    ' data-href="' + productInfo + '"' +
                    ' class="dbg-btn blue half no-shadow dbg-pdb' + (soldOut ? ' disabled' : '') + '">' +
                    (soldOut ? settings.soldOutLabel : shopLabel) + '</a>';

                var productContent;
                if (!suggestion) {
                    productContent = '<div class="dbg-product dbg-color-' + productColor + '" data-product-id="' + item.id + '">' +
                        '<div class="dbg-detail">' +
                        '<figure><a href="' + productInfo + '" data-role="info" title="learn more" target="' + target + '">' +
                        '<img src="' + productImage + '" alt="' + productImageAlt + '" /></a></figure>' +
                        '<a href="' + productInfo + '" data-role="info" title="learn more" class="product-title" target="' + target + '"><h3>' + productName + '</h3></a>' +
                        ratingHtml +
                        priceBlock +
                        '<p>' + productDescription + '</p>' +
                        '</div>' +
                        '<div class="dbg-product-btns">' +
                        shopBtn +
                        '<a href="' + productInfo + '" data-role="info" data-product-id="' + item.id + '" class="dbg-btn learn-more border half" target="' + target + '">' + settings.learnMoreLabel + '</a>' +
                        '</div></div>';
                    $('#dbg').find('#dbg-result .dbg-grid.primary').append(productContent);
                } else {
                    productContent = '<div class="dbg-product dbg-color-' + productColor + '" data-product-id="' + item.id + '">' +
                        '<figure><a href="' + productInfo + '" data-role="info" title="learn more" target="' + target + '">' +
                        '<img src="' + productImage + '" alt="' + productImageAlt + '" /></a></figure>' +
                        '<a href="' + productInfo + '" data-role="info" title="learn more" class="product-title" target="' + target + '"><h3>' + productName + '</h3></a>' +
                        ratingHtml +
                        priceBlock +
                        '<p>' + productDescription + '</p>' +
                        '<a href="' + productInfo + '" data-role="info" data-product-id="' + item.id + '" class="dbg-btn learn-more blue no-shadow" target="' + target + '">' + settings.learnMoreLabel + '</a>' +
                        '</div>';
                    $('#dbg').find('#dbg-result .dbg-grid.suggestion').append(productContent);
                }
                break;
            }
        }
    }

    function dbgGetUrlParameter(sParam) {
        var sPageURL = window.location.search.substring(1),
            sURLVariables = sPageURL.split('&'),
            sParameterName,
            i;

        for (i = 0; i < sURLVariables.length; i++) {
            sParameterName = sURLVariables[i].split('=');

            if (sParameterName[0] === sParam) {
                return sParameterName[1] === undefined ? true : decodeURIComponent(sParameterName[1]);
            }
        }
    }
}());
